import { readonly, ref } from 'vue'
import { BuildVersion } from '@/version'

/**
 * Registering the service worker, and catching the install offer (MT-111).
 *
 * Kept out of the store so that everything touching browser APIs that jsdom
 * does not implement lives in one file with one seam. The store holds what the
 * application reasons about; this holds what the platform does.
 */

/** Set once `beforeinstallprompt` has fired and the offer has not been used. */
const installOffer = ref<BeforeInstallPromptEvent | null>(null)

/** Remembered across visits, so declining once is declining for good. */
const DismissedKey = 'mabelstable.install-dismissed'

const dismissed = ref(read(DismissedKey))

/**
 * What the offer component watches.
 *
 * Two conditions, both necessary. The browser has to have decided this is
 * installable — Chromium fires `beforeinstallprompt` only once it is satisfied,
 * and the application is in no position to second-guess it — and the person
 * must not have declined before. Already-installed is handled earlier, by not
 * listening at all.
 */
export function useInstallOffer() {
  return {
    offer: readonly(installOffer),
    dismissed: readonly(dismissed),
  }
}

/** Shows the browser's own install dialog. Resolves once it has been answered. */
export async function promptInstall(): Promise<void> {
  const event = installOffer.value
  if (!event) return

  // A `beforeinstallprompt` event is single use: once prompted, the same
  // object cannot be prompted again. Cleared first so a double tap cannot try.
  installOffer.value = null

  try {
    await event.prompt()
    await event.userChoice
  } catch {
    // The browser refused — already installed, or the gesture expired. There
    // is nothing useful to say about it, and the offer is gone either way.
  }
}

/** Declining. Remembered, because being asked twice is being nagged. */
export function dismissInstallOffer(): void {
  installOffer.value = null
  dismissed.value = true
  write(DismissedKey)
}

/**
 * Starts listening for the browser's install offer.
 *
 * Separate from registration because the two are independent: a browser may
 * offer installation without this application ever having registered a worker,
 * and the offer has to be caught before anything asks whether it exists.
 */
export function watchForInstallOffer(): void {
  if (isStandalone()) return

  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this the browser shows its own banner, which is the intrusive
    // version SPEC §9.7 rules out. Holding the event is what lets the offer
    // appear inside the family screen, in the product's own words.
    event.preventDefault()
    installOffer.value = event as BeforeInstallPromptEvent
  })

  // Installed from our offer or from the browser's menu — either way there is
  // nothing left to offer.
  window.addEventListener('appinstalled', () => {
    installOffer.value = null
  })
}

function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches === true
}

function read(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== null
  } catch {
    // Private browsing, or storage the person has switched off. Being asked
    // again is a smaller failure than crashing on boot.
    return false
  }
}

function write(key: string): void {
  try {
    window.localStorage.setItem(key, '1')
  } catch {
    // As above. The dismissal holds for this session regardless.
  }
}

/**
 * Registers the worker, and reports when a newer one is waiting.
 *
 * The release travels in the script URL: a deployment changes `?v=`, the
 * browser sees a different script, and the new worker installs and waits. It
 * is never told to skip waiting here — only `applyUpdate` does that, and only
 * because somebody pressed a button.
 */
export function registerServiceWorker(onWaiting: (waiting: boolean) => void): void {
  // Not in development. The dev server rewrites modules on every save, and a
  // worker caching them turns hot reload into a puzzle.
  if (!import.meta.env.PROD) return
  if (!('serviceWorker' in navigator)) return

  // After load, so registering never competes with the first paint for the
  // connection — on the slow cold open this product is judged on (SPEC §22.1).
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register(`/sw.js?v=${encodeURIComponent(BuildVersion)}`, { scope: '/' })
      .then((registration) => {
        if (registration.waiting) onWaiting(true)

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing
          if (!installing) return

          installing.addEventListener('statechange', () => {
            // `installed` with a controller already present means this is a
            // replacement rather than a first install, so there is genuinely
            // something newer sitting behind the one in use.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              onWaiting(true)
            }
          })
        })
      })
      .catch(() => {
        // No worker. The application works; it is just not installable and not
        // available offline, which is not worth telling anybody about.
      })
  })
}

/**
 * Applies a waiting update, then reloads — on a press, never on a timer.
 *
 * `controllerchange` rather than reloading straight away: the new worker has to
 * have taken over before the reload, or the page loads from the old one again
 * and the prompt comes back.
 */
export async function applyUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    window.location.reload()
    return
  }

  const registration = await navigator.serviceWorker.getRegistration('/')
  const waiting = registration?.waiting

  if (!waiting) {
    // The server is newer but no worker is waiting — the skew is in the API,
    // not in the cache. A plain reload fetches the current shell.
    window.location.reload()
    return
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
    once: true,
  })

  waiting.postMessage({ type: 'skip-waiting' })
}

/**
 * The event Chromium fires when it decides a site is installable.
 *
 * Not in the DOM library: it is not a standard, and the browsers that do not
 * implement it simply never fire it. Declared here rather than cast away at the
 * call site so the shape is written down once.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent
    appinstalled: Event
  }
}
